import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import styled from 'styled-components'
import { rem } from 'polished'
import { BANNER_BUTTON_PRIMARY_CONTRAST_COLOR_VAR, BANNER_BUTTON_TEXT_COLOR_VAR } from 'LuxKit/Banners/bannerButtonColorStyles'
import type { PageBannerCarouselItem } from 'LuxKit/Banners/types'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import CookiesContext from 'contexts/CookiesContext'
import {
  dismissedBannerIdsFromCookie,
  LAB_V2_HEADER_BANNER_DISMISSALS_COOKIE,
  recordLabV2HeaderBannerDismissal,
} from 'cookies/labV2HeaderBannerDismissals'
import useIsOfferPage from 'hooks/Navigation/useIsOfferPage'
import useIsSearchPage from 'hooks/Navigation/useIsSearchPage'
import { useLabRouteRequestContext } from 'hooks/useLabRouteRequestContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getImageUrl } from 'lib/image/imageUtils'
import { headerBannersForPage, headerBannersFromUis } from 'lib/labV2/headerBanners'
import { extractReactNodeText } from 'lib/string/extractReactNodeText'
import { useShouldShowBanner } from 'takeovers/utils/hooks/useShouldShowBanner'
import { renderHeaderBannerRichText } from './headerBannerRichText'
import { getTakeoverBannerLocation } from './NotificationBannerV3.utils'
import { labV2HeaderBannerItem, type LabV2HeaderBannerCta } from './labV2HeaderBannerItem'

const BANNER_DISMISSAL_PERIOD_DAYS = 7

const HeaderBannerLeadingIcon = styled.img`
  height: ${rem(44)};
  width: auto;
  max-width: ${rem(132)};
  object-fit: contain;
  display: block;
`

function bannerActionButtonProps(cta: LabV2HeaderBannerCta) {
  return {
    kind: cta.style,
    size: 'small' as const,
    to: cta.path,
    children: cta.label,
  }
}

function bannerDescription(desktop: string, mobile?: string) {
  if (!mobile || mobile === desktop) {
    return <>{renderHeaderBannerRichText(desktop)}</>
  }

  return <>
    <CSSBreakpoint as="span" min="tablet">
      {renderHeaderBannerRichText(desktop)}
    </CSSBreakpoint>
    <CSSBreakpoint as="span" max="mobile">
      {renderHeaderBannerRichText(mobile)}
    </CSSBreakpoint>
  </>
}

function desktopAlignFor(description: string): 'start' | 'center' {
  return extractReactNodeText(renderHeaderBannerRichText(description)).length > 80 ? 'start' : 'center'
}

export default function useLabV2HeaderBannerItems(): Array<PageBannerCarouselItem> {
  const uis = useAppSelector(state => state.system.features.labV2?.uis)
  const { pathname, currentRegionCode } = useLabRouteRequestContext()
  const { getCookie } = useContext(CookiesContext)
  const isSearchPage = useIsSearchPage()
  const isOfferPage = useIsOfferPage()
  const shouldShowTakeoverBanner = useShouldShowBanner({
    location: getTakeoverBannerLocation(isSearchPage, isOfferPage),
  })
  const [dismissedBannerIds, setDismissedBannerIds] = useState<Set<string>>(
    () => dismissedBannerIdsFromCookie(getCookie(LAB_V2_HEADER_BANNER_DISMISSALS_COOKIE)),
  )

  const refreshDismissedBannerIds = useCallback(() => {
    const next = dismissedBannerIdsFromCookie(getCookie(LAB_V2_HEADER_BANNER_DISMISSALS_COOKIE))
    setDismissedBannerIds(current => {
      if (current.size === next.size && [...next].every(id => current.has(id))) {
        return current
      }
      return next
    })
  }, [getCookie])

  const dismissBanner = useCallback((bannerId: string) => {
    const endDate = moment().add(BANNER_DISMISSAL_PERIOD_DAYS, 'days').toISOString()
    recordLabV2HeaderBannerDismissal(bannerId, endDate)
    refreshDismissedBannerIds()
  }, [refreshDismissedBannerIds])

  const banners = useMemo(
    () => headerBannersForPage(headerBannersFromUis(uis), pathname, dismissedBannerIds, currentRegionCode),
    [uis, pathname, dismissedBannerIds, currentRegionCode],
  )

  useEffect(() => {
    if (banners.length === 0) {
      return
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshDismissedBannerIds()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [banners.length, refreshDismissedBannerIds])

  return useMemo(() => {
    if (shouldShowTakeoverBanner) {
      return []
    }

    return banners.map((banner): PageBannerCarouselItem => {
      const item = labV2HeaderBannerItem(banner)
      return {
        id: item.id,
        kind: item.kind,
        onDismiss: () => dismissBanner(item.id),
        icon: item.iconImageId ? (
          <HeaderBannerLeadingIcon src={getImageUrl(item.iconImageId, { height: 88, quality: 'good' })} alt="" aria-hidden="true" />
        ) : undefined,
        children: bannerDescription(item.description.desktop, item.description.mobile),
        desktopAlign: item.description.mobile && item.description.mobile !== item.description.desktop
          ? desktopAlignFor(item.description.desktop)
          : undefined,
        actionButtonProps: item.cta ? bannerActionButtonProps(item.cta) : undefined,
        mobileActionButtonProps: item.mobileCta ? bannerActionButtonProps(item.mobileCta) : undefined,
        style: item.isCustomAppearance ? {
          background: item.backgroundCss,
          color: item.textColor,
          [BANNER_BUTTON_TEXT_COLOR_VAR]: item.textColor,
          [BANNER_BUTTON_PRIMARY_CONTRAST_COLOR_VAR]: item.ctaPrimaryTextColor,
        } : undefined,
      }
    })
  }, [
    shouldShowTakeoverBanner,
    banners,
    dismissBanner,
  ])
}
